import { getRateConfigsApi } from '@nxsys/services/api/umbrella-rate-calculator';
import { NmwAgeBandField } from '@nxsys/types/models/kid/umbrella-rate-calculator';
import { useEffect, useState } from 'react';

type NmwRatesState = {
  taxYear: number | null;
  fields: NmwAgeBandField[];
  loading: boolean;
  error: boolean;
};

/**
 * NX-5971: NMW rates for one tax year, from the backend table the payroll NMW
 * check uses. Only the response for the year currently asked for is ever shown:
 * a superseded request is ignored when it lands, and a failed one clears the
 * rows instead of leaving the previous year's rates under the new year.
 */
export const useNmwRatesByTaxYear = (taxYear: number | null) => {
  const [state, setState] = useState<NmwRatesState>({
    taxYear: null,
    fields: [],
    loading: false,
    error: false,
  });

  useEffect(() => {
    if (!taxYear) return undefined;

    let current = true;
    setState({ taxYear, fields: [], loading: true, error: false });

    getRateConfigsApi(taxYear)
      .then((response) => {
        if (!current) return;
        setState({
          taxYear,
          fields: response?.nmwRates?.fields ?? [],
          loading: false,
          error: false,
        });
      })
      .catch(() => {
        if (!current) return;
        setState({ taxYear, fields: [], loading: false, error: true });
      });

    return () => {
      current = false;
    };
  }, [taxYear]);

  // Belt and braces: never hand back rows that belong to another year
  const matches = state.taxYear === taxYear;
  return {
    fields: matches ? state.fields : [],
    loading: matches ? state.loading : !!taxYear,
    error: matches && state.error,
  };
};
